import { createEffect, createEvent, createStore, sample } from 'effector';
import { httpClient, HttpError } from '@/api';

export type OnboardForm = {
  // Раздельные части ФИО — раньше было слитное `name`, и backend сплитил
  // его эвристикой со склейкой last_name+patronymic. Теперь поля
  // вводятся раздельно.
  first_name: string;
  last_name: string;
  patronymic: string;
  // Полная дата рождения в формате ISO `YYYY-MM-DD`. Обязательна — без
  // неё кросс-валидатор в osint-portfolio не может надёжно отличить
  // subject'а от однофамильцев. Валидация: не пустая, год >= 1900,
  // не в будущем (см. Onboard.tsx).
  birth_date: string;
  // Опциональные identifier'ы — без них cross-validator в osint-portfolio
  // не может отличить subject'а от однофамильцев. Одиночные строки;
  // если пользователь хочет несколько значений — пишет через запятую,
  // backend сделает splitMultiline.
  email: string;
  phone: string;
  // Множество ников: у одного человека легко 2–5 разных handle'ов
  // (рабочий/личный, github vs telegram, старый-молодой). Раньше было
  // одно поле — пользователь не мог указать больше одного. Каждый ник
  // в osint-pipeline уезжает как usernames[].push({site:'any', handle}),
  // чтобы Maigret/username-enum прогнал по 3000+ сайтам.
  nicknames: string[];
  role: string;
  city: string;
  about: string;
  topics: string[];
  tone: string;
  handles: { linkedin: string; github: string; telegram: string; site: string; x: string };
};

export const TOTAL_STEPS = 3;

const empty: OnboardForm = {
  first_name: '',
  last_name: '',
  patronymic: '',
  birth_date: '',
  email: '',
  phone: '',
  nicknames: [],
  role: '',
  city: '',
  about: '',
  topics: [],
  tone: '',
  handles: { linkedin: '', github: '', telegram: '', site: '', x: '' },
};

export const formHydrated = createEvent<Partial<OnboardForm>>();
export const fieldChanged = createEvent<{
  key: keyof Omit<OnboardForm, 'topics' | 'tone' | 'handles' | 'nicknames'>;
  value: string;
}>();
export const topicToggled = createEvent<string>();
export const toneSelected = createEvent<string>();
export const handleChanged = createEvent<{ key: keyof OnboardForm['handles']; value: string }>();
export const nicknameAdded = createEvent<string>();
export const nicknameRemoved = createEvent<number>();

export const $form = createStore<OnboardForm>(empty)
  .on(formHydrated, (current, next) => ({ ...current, ...next }))
  .on(fieldChanged, (s, { key, value }) => ({ ...s, [key]: value }))
  .on(topicToggled, (s, t) => ({
    ...s,
    topics: s.topics.includes(t) ? s.topics.filter((x) => x !== t) : [...s.topics, t],
  }))
  .on(toneSelected, (s, t) => ({ ...s, tone: t }))
  .on(handleChanged, (s, { key, value }) => ({ ...s, handles: { ...s.handles, [key]: value } }))
  .on(nicknameAdded, (s, raw) => {
    const v = raw.trim().replace(/^@/, '');
    if (!v) return s;
    if (s.nicknames.includes(v)) return s;
    return { ...s, nicknames: [...s.nicknames, v] };
  })
  .on(nicknameRemoved, (s, idx) => ({
    ...s,
    nicknames: s.nicknames.filter((_, i) => i !== idx),
  }));

export const $formHydrated = createStore(false).on(formHydrated, () => true);

export const $activeHandles = $form.map((f) =>
  (Object.entries(f.handles) as [keyof OnboardForm['handles'], string][])
    .filter(([, v]) => v.trim().length > 0)
    .map(([k, v]) => ({ name: k, value: v.trim() })),
);

const draftSaveRequested = createEvent<OnboardForm>();

export const saveDraftFx = createEffect<OnboardForm, Partial<OnboardForm>>((form) => {
  return httpClient.put<Partial<OnboardForm>>('/api/lume/onboard/draft', form);
});

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DRAFT_DEBOUNCE_MS = 800;

sample({
  clock: [fieldChanged, topicToggled, toneSelected, handleChanged, nicknameAdded, nicknameRemoved],
  source: $form,
  filter: $formHydrated,
  target: draftSaveRequested,
});

draftSaveRequested.watch((form) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    saveDraftFx(form).catch((err) => {
      if (err instanceof HttpError && err.status === 401) {
        // не залогинен — черновик не сохранится; ОК для public preview
        return;
      }
      console.warn('saveDraftFx failed', err);
    });
  }, DRAFT_DEBOUNCE_MS);
});
